import React, { useState } from 'react';
import { getStatusColor } from '../../utils/appointmentUtils';
import { getTimezoneDisplayLabel } from '../utils/timezone';
import '../../components/appointments/styles/AppointmentDetailsPageLayout.css';

const formatStatusLabel = (status, fallback = 'Pending') => {
  if (!status) return fallback;
  return String(status)
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Card-based layout for meeting details page (matches AppointmentDetailsPageLayout UX/UI).
 */
const MeetingDetailsPageLayout = ({
  meeting,
  formatSafe,
  onCancel,
  onOpenReschedule,
  onAddClient,
  addingClient = false,
  addClientError = null,
  allowAddClient = false,
}) => {
  const {
    title,
    clientName,
    clientEmail,
    clientPhone,
    additionalClients = [],
    notes,
    status,
    duration,
    meetingType,
    location,
    videoConferencingLink,
    timezone,
    rescheduleReason,
  } = meeting;

  const isCancelled = (status || '').toLowerCase() === 'cancelled';
  const meetingTypeLabel = (meetingType || 'one_on_one') === 'group' ? 'Group meeting' : 'One on One';

  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');

  const handleOpenAddClient = () => {
    setShowAddClientForm(true);
    setNewClientName('');
    setNewClientEmail('');
  };

  const handleCancelAddClient = () => {
    setShowAddClientForm(false);
    setNewClientName('');
    setNewClientEmail('');
  };

  const handleSubmitAddClient = (e) => {
    e.preventDefault();
    const name = newClientName.trim();
    const email = newClientEmail.trim();
    if (!name || !email || !onAddClient) return;
    onAddClient({ name, email });
    setShowAddClientForm(false);
    setNewClientName('');
    setNewClientEmail('');
  };

  const startDate = meeting.startDate;
  const endDate = meeting.endDate;

  return (
    <div className="adp-layout">
      <div className="adp-layout__hero">
        <div className="adp-layout__hero-top">
          <div className="adp-layout__hero-left">
            <h2 className="adp-layout__client-name">{title || 'Meeting'}</h2>
            <div className="adp-layout__chips">
              <span
                className="adp-layout__chip"
                style={{ backgroundColor: getStatusColor(status || 'pending'), color: '#fff' }}
              >
                {formatStatusLabel(status, 'Pending')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="adp-layout__grid">
        <div className="adp-layout__card">
          <div className="adp-layout__card-body">
            <section className="adp-layout__detail-section">
              <span className="adp-layout__section-title">Clients</span>
              <div className="adp-layout__row adp-layout__row--primary">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <p className="adp-layout__text adp-layout__text--bold">
                    {clientName || 'N/A'}
                    <span className="adp-layout__text adp-layout__text--secondary" style={{ fontWeight: 400, marginLeft: 4 }}>
                      (Primary)
                    </span>
                  </p>
                  {clientEmail && (
                    <a href={`mailto:${clientEmail}`} className="adp-layout__link adp-layout__contact-subtext">
                      {clientEmail}
                    </a>
                  )}
                  {clientPhone && (
                    <a
                      href={`tel:${clientPhone}`}
                      className="adp-layout__link adp-layout__contact-subtext"
                      style={{ display: 'block' }}
                    >
                      {clientPhone}
                    </a>
                  )}
                  {!clientEmail && !clientPhone && (
                    <p className="adp-layout__text adp-layout__text--secondary">No contact info</p>
                  )}
                </div>
              </div>
              {additionalClients.map((c, idx) => (
                <div key={idx} className="adp-layout__row" style={{ marginTop: 8 }}>
                  <div>
                    <p className="adp-layout__text adp-layout__text--bold">{c.name || 'N/A'}</p>
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="adp-layout__link adp-layout__contact-subtext">
                        {c.email}
                      </a>
                    ) : (
                      <p className="adp-layout__text adp-layout__text--secondary">No email</p>
                    )}
                  </div>
                </div>
              ))}
              {allowAddClient && onAddClient && (
                <div className="adp-layout__detail-section" style={{ marginTop: 16 }}>
                  {!showAddClientForm ? (
                    <button
                      type="button"
                      className="adp-layout__complete-link"
                      onClick={handleOpenAddClient}
                      disabled={addingClient}
                    >
                      Add another client
                    </button>
                  ) : (
                    <form onSubmit={handleSubmitAddClient} noValidate className="meeting-form">
                      <div className="adp-layout__row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <input
                          type="text"
                          className="meeting-form__input"
                          placeholder="Name"
                          value={newClientName}
                          onChange={(e) => setNewClientName(e.target.value)}
                          required
                          disabled={addingClient}
                          style={{ flex: '1 1 120px', minWidth: 0 }}
                        />
                        <input
                          type="email"
                          className="meeting-form__input"
                          placeholder="Email"
                          value={newClientEmail}
                          onChange={(e) => setNewClientEmail(e.target.value)}
                          required
                          disabled={addingClient}
                          style={{ flex: '1 1 140px', minWidth: 0 }}
                        />
                      </div>
                      <div className="adp-layout__row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="submit"
                          className="adp-layout__btn adp-layout__btn--success"
                          disabled={addingClient || !newClientName.trim() || !newClientEmail.trim()}
                        >
                          {addingClient ? 'Adding…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="adp-layout__btn adp-layout__btn--secondary"
                          onClick={handleCancelAddClient}
                          disabled={addingClient}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                  {addClientError && (
                    <p className="adp-layout__text adp-layout__text--secondary" style={{ marginTop: 8, color: '#c62828' }}>
                      {addClientError}
                    </p>
                  )}
                </div>
              )}
            </section>

            <section className="adp-layout__detail-section">
              <span className="adp-layout__section-title">Time</span>
              <div className="adp-layout__row">
                <div className="adp-layout__datetime">
                  <p className="adp-layout__text">
                    {formatSafe(startDate, 'MMM d, yyyy, h:mm a')}
                    {endDate && ` - ${formatSafe(endDate, 'h:mm a')}`}
                  </p>
                </div>
              </div>
            </section>
            {duration != null && duration > 0 && (
              <section className="adp-layout__detail-section">
                <span className="adp-layout__section-title">Duration</span>
                <div className="adp-layout__row">
                  <p className="adp-layout__text">{duration} min</p>
                </div>
              </section>
            )}
            {timezone && (
              <section className="adp-layout__detail-section">
                <span className="adp-layout__section-title">Timezone</span>
                <div className="adp-layout__row">
                  <p className="adp-layout__text">{getTimezoneDisplayLabel(timezone)}</p>
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="adp-layout__right-stack">
          <div className="adp-layout__card adp-layout__card--meeting-details">
            <span className="adp-layout__card-title">Meeting details</span>
            <div className="adp-layout__card-body">
              <section className="adp-layout__detail-section">
                <span className="adp-layout__section-title">Title</span>
                <div className="adp-layout__row">
                  <p className="adp-layout__text">{title || 'Meeting'}</p>
                </div>
              </section>
              <section className="adp-layout__detail-section">
                <span className="adp-layout__section-title">Meeting type</span>
                <div className="adp-layout__row">
                  <p className="adp-layout__text">{meetingTypeLabel}</p>
                </div>
              </section>
              {location && (
                <section className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Location</span>
                  <div className="adp-layout__row">
                    <p className="adp-layout__text">{location}</p>
                  </div>
                </section>
              )}
              {videoConferencingLink && (
                <section className="adp-layout__detail-section">
                  <span className="adp-layout__section-title">Video conferencing</span>
                  <div className="adp-layout__row">
                    <a
                      href={videoConferencingLink.startsWith('http') ? videoConferencingLink : `https://${videoConferencingLink}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="adp-layout__link"
                    >
                      {videoConferencingLink}
                    </a>
                  </div>
                </section>
              )}
            </div>
          </div>

          {(onCancel || onOpenReschedule) && !isCancelled && (
            <div className="adp-layout__card adp-layout__card--actions">
              <span className="adp-layout__card-title">Actions</span>
              <div className="adp-layout__card-body">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%' }}>
                  {onOpenReschedule && (
                    <button
                      type="button"
                      className="adp-layout__btn adp-layout__btn--primary"
                      onClick={onOpenReschedule}
                      style={{ flex: '1 1 180px' }}
                    >
                      Reschedule meeting
                    </button>
                  )}
                  {onCancel && (
                    <button
                      type="button"
                      className="adp-layout__btn adp-layout__btn--cancel"
                      onClick={onCancel}
                      style={{ flex: '1 1 180px', backgroundColor: '#dc3545', color: '#fff' }}
                    >
                      Cancel meeting
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {(notes || (rescheduleReason && String(rescheduleReason).trim())) && (
          <div className="adp-layout__card adp-layout__card--full">
            <span className="adp-layout__card-title">Notes</span>
            <div className="adp-layout__card-body">
              {notes && (
                <p className="adp-layout__text adp-layout__text--secondary">
                  {notes}
                </p>
              )}
              {rescheduleReason && String(rescheduleReason).trim() && (
                <div style={{ marginTop: notes ? 12 : 0 }}>
                  <span className="adp-layout__section-title">Reschedule reason</span>
                  <p className="adp-layout__text adp-layout__text--secondary">
                    {String(rescheduleReason).trim()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingDetailsPageLayout;
